import { confirmDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { TaxAdvisorImportAPI } from '../../../accounting-import/api/AccountingImportAPI.js';
import { UsageAPI } from '../../api/UsageAPI.js';
import { renderDashboardCardHeader, renderDashboardLoadingCard, renderLiveBadge } from './StatRenderUtilities.js';

/**
 * The runs that may only happen once at a time. A held lock means a run is in progress — or that one crashed without
 * letting go, which is what the release button is for.
 */
export const ImportExportLocksSection = stateful(() => {
  const { value: isPersonImportLocked, loading: isPersonImportLockedLoading } = useQuery(
    () => UsageAPI.isPersonImportLocked()(),
    [],
  );
  const { value: isSepaExportLocked, loading: isSepaExportLockedLoading } = useQuery(
    () => UsageAPI.isSepaExportLocked()(),
    [],
  );
  const { value: isAccountingImportLocked, loading: isAccountingImportLockedLoading } = useQuery(
    () => TaxAdvisorImportAPI.subscribeLock()(),
    [],
  );

  if (isPersonImportLockedLoading || isSepaExportLockedLoading || isAccountingImportLockedLoading) {
    return renderDashboardLoadingCard();
  }

  const runs = [
    {
      title: 'Personenimport',
      iconClass: 'icon-[material-symbols--person-add-outline-rounded]',
      locked: isPersonImportLocked,
      unlock: async () => {
        if (
          await confirmDialog(
            'Möchten Sie den Personenimport wirklich freigeben? Das ist nur für den Notfall gedacht, wenn ein abgebrochener Import die Sperre nicht gelöst hat.',
          )
        ) {
          await UsageAPI.unlockPersonImport()();
        }
      },
    },
    {
      title: 'SEPA-Export',
      iconClass: 'icon-[material-symbols--payments-outline-rounded]',
      locked: isSepaExportLocked,
      unlock: async () => {
        if (
          await confirmDialog(
            'Möchten Sie den SEPA-Export wirklich freigeben? Das ist nur für den Notfall gedacht, wenn ein abgebrochener Export die Sperre nicht gelöst hat.',
          )
        ) {
          await UsageAPI.unlockSepaExport()();
        }
      },
    },
    // released by the import itself, also when it fails
    {
      title: 'Buchhaltungsimport',
      iconClass: 'icon-[material-symbols--account-balance-outline-rounded]',
      locked: isAccountingImportLocked,
      unlock: undefined,
    },
  ];

  return html`
    <div class="card h-full">
      <div class="card-body gap-2">
        ${renderDashboardCardHeader(
          'Importe & Exporte',
          'icon-[material-symbols--sync-outline-rounded]',
          renderLiveBadge(),
          'Ein laufender Vorgang sperrt weitere Starts, bis er fertig ist.',
        )}

        <ul class="flex flex-col divide-y divide-base-300">
          ${runs.map(
            ({ title, iconClass, locked, unlock }) => html`
              <li class="flex flex-wrap items-center gap-3 py-3">
                <span class="flex size-10 shrink-0 items-center justify-center rounded-field bg-base-200">
                  <span class=${`${iconClass} size-6 text-base-content/70`}></span>
                </span>
                <span class="min-w-32 flex-1 font-semibold">${title}</span>
                <span class="ml-auto flex items-center gap-2">
                  ${
                    locked
                      ? html`<span class="badge badge-info">Läuft</span>`
                      : html`<span class="badge badge-ghost">Bereit</span>`
                  }
                  ${
                    locked && unlock
                      ? html`
                          <button class="btn btn-sm btn-ghost" @click=${unlock}>
                            <span class="icon-[material-symbols--lock-open-outline-rounded] size-5"></span>
                            Freigeben
                          </button>
                        `
                      : nothing
                  }
                </span>
              </li>
            `,
          )}
        </ul>
      </div>
    </div>
  `;
});
